// Pure display formatters for the Results Interpretation Panel (Story 9.7, AC3). Unit-tested.
//
// Decimal DISCIPLINE: measures arrive as exact decimal STRINGS. These formatters render that
// verbatim precision — they NEVER re-round a clinical value through float math. The one place a
// number is derived is the `<1.0` percentile THRESHOLD compare, and even there the DISPLAYED
// output is a fixed string, never a re-rendered float of the stored value.

/** Em-dash for an absent measure — never `0`, never blank (AC3). */
export const EM_DASH = "—";

/** Helper line shown when a scale is outside the normative range (the NULL std/percentile pair). */
export const OUT_OF_RANGE_LABEL = "Outside normative range";

/** Render a logit verbatim at stored precision; em-dash when absent (never expected for a logit). */
export function formatLogit(value: string | null): string {
  return value ?? EM_DASH;
}

/** Render a standard score verbatim at stored precision; em-dash when out-of-range (null). */
export function formatStandardScore(value: string | null): string {
  return value ?? EM_DASH;
}

/**
 * Render a percentile. Below 1% renders the legacy `<1.0` convention (Spencer sign-off — the
 * STORED value stays the true numeric, this is display only); em-dash when out-of-range (null).
 * The threshold compare uses `Number` for the `< 1` decision ONLY; the value shown is always a
 * fixed string (`"<1.0"`) or the verbatim stored string — never a floated re-render.
 */
export function formatPercentile(value: string | null): string {
  if (value === null) return EM_DASH;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric < 1) return "<1.0";
  return value;
}

/**
 * True when a scale is outside the normative range: its standard score AND percentile are both
 * null (the "NULL-triple with non-null logits" marker — the client's age had no matching band).
 * Per-scale though age applies to the whole client, so both scales null together in practice;
 * kept per-scale for a defensive, symmetric render.
 */
export function isScaleOutOfRange(
  std: string | null,
  percentile: string | null,
): boolean {
  return std === null && percentile === null;
}
